/**
 * Fonte única das cores do app, nos dois temas.
 *
 * - `tailwind.config.js` lê daqui para gerar as classes (`bg-neutral-100`,
 *   `text-error-600`…) como variáveis CSS que trocam de valor no tema escuro.
 * - `hooks/use-theme-colors.ts` lê daqui para os poucos lugares que só aceitam
 *   string (cor de ícone, `placeholderTextColor`, gráficos, Stripe).
 *
 * O tema claro é a paleta de sempre. O escuro espelha cada escala em torno do
 * meio: o tom claro de fundo (50/100/200) vira um tom escuro tingido, e o tom
 * escuro de texto (700/800/900) vira o claro. Assim `bg-success-50
 * text-success-700` continua legível sem nenhum `dark:` na tela.
 *
 * CommonJS de propósito: o Tailwind carrega o config fora do bundler.
 */
const twColors = require('tailwindcss/colors');

const STEPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];

/**
 * Opções de fundo do tema escuro. O laranja é o mesmo em todas; muda só a
 * família de cinzas (fundo, card, bordas, texto). `ACTIVE_DARK` escolhe qual vale.
 */
const DARK_VARIANTS = {
  // Preto quente puxado do #242120 da marca.
  quente: {
    background: '#100F0E',
    card: '#1A1817',
    ink: '#2A2624',
    neutral: ['#1D1B1A', '#262322', '#322F2D', '#45413F', '#716C69', '#9E9895', '#BBB6B3', '#D4D0CD', '#E8E5E3', '#F5F3F2', '#FBFAF9'],
  },
  // Grafite neutro, levemente frio: o laranja não divide matiz com o fundo.
  grafite: {
    background: '#0F1012',
    card: '#18191C',
    ink: '#24262A',
    neutral: ['#1E1F23', '#25272B', '#303237', '#43464C', '#6E7178', '#9A9DA4', '#B7BAC0', '#D1D3D7', '#E6E7EA', '#F4F4F6', '#FAFAFB'],
  },
  // Preto puro (OLED): máximo contraste, cards em cinza bem escuro.
  preto: {
    background: '#000000',
    card: '#121212',
    ink: '#1C1C1C',
    neutral: ['#1A1A1A', '#222222', '#2E2E2E', '#404040', '#6B6B6B', '#999999', '#B5B5B5', '#D0D0D0', '#E5E5E5', '#F5F5F5', '#FAFAFA'],
  },
  // Azul-noite: complementar do laranja, é o que mais faz ele saltar.
  noite: {
    background: '#0B1018',
    card: '#121A26',
    ink: '#1A2433',
    neutral: ['#18212F', '#1F2937', '#2A3546', '#3B475A', '#64718A', '#94A0B5', '#B4BDCC', '#CFD5DF', '#E3E7ED', '#F1F4F8', '#F8FAFC'],
  },
  // Cinza-carvão estilo iOS: escuro mais claro, menos contraste duro.
  carvao: {
    background: '#1C1C1E',
    card: '#2C2C2E',
    ink: '#3A3A3C',
    neutral: ['#333336', '#3A3A3C', '#48484A', '#5A5A5E', '#7C7C80', '#A1A1A6', '#BDBDC2', '#D5D5D9', '#E8E8EC', '#F5F5F7', '#FBFBFD'],
  },
};

const ACTIVE_DARK = 'carvao';

function hexToRgb(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

function rgbToHex(rgb) {
  return `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** `amount` de `a` sobre `b` (0 = só b, 1 = só a). */
function mix(a, b, amount) {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex(ca.map((v, i) => v * amount + cb[i] * (1 - amount)));
}

/** Espelha uma escala clara para o tema escuro. */
function darkScale(light, DARK_BACKGROUND) {
  return {
    50: mix(light['900'], DARK_BACKGROUND, 0.28),
    100: mix(light['900'], DARK_BACKGROUND, 0.42),
    200: mix(light['800'], DARK_BACKGROUND, 0.55),
    300: light['700'],
    400: light['400'],
    500: light['500'],
    // 600 é o tom mais usado como TEXTO de status (`text-error-600`): sobe um
    // pouco para ler no fundo escuro sem perder o branco por cima quando é botão.
    600: mix(light['500'], light['400'], 0.5),
    700: light['300'],
    800: light['200'],
    900: light['100'],
    950: light['50'],
  };
}

function withBase(custom, base) {
  const out = {};
  for (const step of STEPS) out[step] = (custom[step] ?? base[step]).toUpperCase();
  return out;
}

const lightScales = {
  primary: withBase(
    {
      50: '#FFF4ED',
      100: '#FFE5D4',
      200: '#FFC7A8',
      300: '#FF9F71',
      400: '#FF6B3D',
      500: '#FD5001',
      600: '#E64500',
      700: '#CC3C00',
      800: '#B33300',
      900: '#992B00',
      950: '#4D1600',
    },
    twColors.orange,
  ),
  secondary: withBase(
    {
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F29C11',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
    },
    twColors.amber,
  ),
  premium: withBase(
    {
      50: '#FFF1F2',
      100: '#FFE4E6',
      200: '#FECDD3',
      300: '#FDA4AF',
      400: '#FB7185',
      500: '#AF340B',
      600: '#9F2E0A',
      700: '#8F2809',
      800: '#7F2208',
      900: '#6F1C07',
      950: '#3A0E03',
    },
    twColors.rose,
  ),
  success: withBase({ 50: '#F0FDF4', 100: '#DCFCE7', 500: '#22C55E', 600: '#16A34A' }, twColors.green),
  warning: withBase({ 50: '#FFFBEB', 100: '#FEF3C7', 500: '#F59E0B', 600: '#D97706' }, twColors.amber),
  error: withBase({ 50: '#FEF2F2', 100: '#FEE2E2', 500: '#EF4444', 600: '#DC2626' }, twColors.red),
  info: withBase({ 50: '#EFF6FF', 100: '#DBEAFE', 500: '#3B82F6', 600: '#2563EB' }, twColors.blue),
};

// Cores padrão do Tailwind que aparecem nas telas: entram na mesma troca.
for (const hue of [
  'gray', 'zinc', 'slate', 'stone', 'red', 'orange', 'amber', 'yellow', 'lime', 'green',
  'emerald', 'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple', 'fuchsia', 'pink', 'rose',
]) {
  lightScales[hue] = withBase({}, twColors[hue]);
}

lightScales.neutral = withBase({}, twColors.neutral);

/** Escalas e superfícies do escuro para uma das `DARK_VARIANTS`. */
function buildDark(name) {
  const v = DARK_VARIANTS[name];
  const scales = {};
  for (const [hue, scale] of Object.entries(lightScales)) scales[hue] = darkScale(scale, v.background);
  // Cinzas: escala própria da variante, não o inverso mecânico (que deixaria
  // bordas e fundos sutis contrastados demais).
  scales.neutral = Object.fromEntries(STEPS.map((step, i) => [step, v.neutral[i]]));
  // Cinzas frios do Tailwind seguem o mesmo neutro: no escuro não há diferença útil.
  for (const hue of ['gray', 'zinc', 'slate', 'stone']) scales[hue] = scales.neutral;
  const surfaces = {
    background: v.background,
    canvas: v.background,
    foreground: v.neutral[9],
    card: v.card,
    'card-foreground': v.neutral[9],
    border: v.neutral[2],
    input: v.neutral[2],
    accent: '#FF6B3D',
    ink: v.ink,
    'safe-area-background': v.background,
  };
  return { scales, surfaces };
}

const activeDark = buildDark(ACTIVE_DARK);
const darkScales = activeDark.scales;

/** Superfícies e texto base (`bg-background`, `bg-card`, `text-foreground`…). */
const semantic = {
  light: {
    background: '#FFFFFF',
    // Fundo das páginas logadas (o antigo `bg-[#f5f5f5]`): cards ficam por cima.
    canvas: '#F5F5F5',
    foreground: '#000000',
    card: '#FFFFFF',
    'card-foreground': '#000000',
    border: '#E5E5E5',
    input: '#E5E5E5',
    accent: '#AF340B',
    // Bloco escuro de destaque (hero de valores, tooltip): continua escuro no
    // tema escuro, só um degrau acima do card para não sumir.
    ink: '#171717',
    'safe-area-background': '#F5F5F5',
  },
  dark: activeDark.surfaces,
};

module.exports = {
  STEPS,
  lightScales,
  darkScales,
  semantic,
  hexToRgb,
  mix,
  DARK_VARIANTS,
  ACTIVE_DARK,
  buildDark,
};
